<script setup>
import { computed } from 'vue'

const props = defineProps({
  show: {
    type: Boolean,
    default: false
  },
  event: {
    type: Object,
    default: null
  },
  ou1Balanced: {
    type: Object,
    default: null
  },
  ou2Balanced: {
    type: Object,
    default: null
  }
})

const emit = defineEmits(['close'])

const ou1Lines = computed(() => {
  if (!props.event || !props.event.ou1Lines) return []
  
  const linesData = props.event.ou1Lines
  if (!linesData || typeof linesData !== 'object') return []
  
  // Convert to array and sort by line value
  return Object.entries(linesData)
    .map(([line, odds]) => ({
      line: parseFloat(line),
      over: odds.over,
      under: odds.under,
      diff: Math.abs(odds.over - odds.under),
      isBalanced: props.ou1Balanced && props.ou1Balanced.line === parseFloat(line)
    }))
    .sort((a, b) => a.line - b.line)
})

const ou2Lines = computed(() => {
  if (!props.event || !props.event.ou2Lines) return []
  
  const linesData = props.event.ou2Lines
  if (!linesData || typeof linesData !== 'object') return []
  
  // Convert to array and sort by line value
  return Object.entries(linesData)
    .map(([line, odds]) => ({
      line: parseFloat(line),
      over: odds.over,
      under: odds.under,
      diff: Math.abs(odds.over - odds.under),
      isBalanced: props.ou2Balanced && props.ou2Balanced.line === parseFloat(line)
    }))
    .sort((a, b) => a.line - b.line)
})

const closeModal = () => {
  emit('close')
}
</script>

<template>
  <Transition
    enter-active-class="transition duration-300 ease-out"
    enter-from-class="opacity-0"
    enter-to-class="opacity-100"
    leave-active-class="transition duration-200 ease-in"
    leave-from-class="opacity-100"
    leave-to-class="opacity-0"
  >
    <div 
      v-if="show" 
      class="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
      @click.self="closeModal"
    >
      <Transition
        enter-active-class="transition duration-300 ease-out transform"
        enter-from-class="scale-95 opacity-0"
        enter-to-class="scale-100 opacity-100"
        leave-active-class="transition duration-200 ease-in transform"
        leave-from-class="scale-100 opacity-100"
        leave-to-class="scale-95 opacity-0"
      >
        <div 
          v-if="show"
          class="bg-white rounded-lg shadow-xl max-w-6xl w-full max-h-[90vh] flex flex-col"
        >
          <!-- Header -->
          <div class="flex items-center justify-between p-6 border-b border-gray-200">
            <h2 class="text-xl font-semibold text-gray-900">
              All Balanced Lines
            </h2>
            <button 
              @click="closeModal"
              class="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <i class="fas fa-times text-xl"></i>
            </button>
          </div>

          <!-- Content -->
          <div class="flex-1 overflow-y-auto p-6">
            <div class="grid grid-cols-2 gap-6">
              <!-- OU1 Lines Table -->
              <div>
                <h3 class="text-lg font-semibold text-gray-800 mb-4 text-center">
                  {{ event?.ocHomeTeamName || event?.home || 'Home' }} - OU1 Lines
                </h3>
                <div v-if="ou1Lines.length === 0" class="text-center py-8 text-gray-500">
                  No lines available
                </div>
                
                <div v-else class="space-y-2">
                  <!-- Table Header -->
                  <div class="grid grid-cols-4 gap-2 pb-2 border-b border-gray-200 font-semibold text-xs text-gray-600">
                    <div>Line</div>
                    <div class="text-green-600">Over</div>
                    <div class="text-red-600">Under</div>
                    <div>Diff</div>
                  </div>
                  
                  <!-- Table Rows -->
                  <div
                    v-for="(line, index) in ou1Lines"
                    :key="index"
                    :class="[
                      'grid grid-cols-4 gap-2 py-2 px-2 rounded-lg transition-colors text-sm',
                      line.isBalanced 
                        ? 'bg-blue-50 border-2 border-blue-500 font-semibold' 
                        : 'hover:bg-gray-50'
                    ]"
                  >
                    <div :class="['font-medium', line.isBalanced ? 'text-blue-700' : 'text-gray-900']">
                      {{ line.line }}
                      <span v-if="line.isBalanced" class="ml-1 text-xs text-blue-600">
                        <i class="fas fa-star"></i>
                      </span>
                    </div>
                    <div :class="['text-green-600', line.isBalanced ? 'font-bold' : 'font-medium']">
                      {{ line.over }}
                    </div>
                    <div :class="['text-red-600', line.isBalanced ? 'font-bold' : 'font-medium']">
                      {{ line.under }}
                    </div>
                    <div :class="['text-gray-600 text-xs', line.isBalanced ? 'font-bold' : '']">
                      {{ line.diff.toFixed(3) }}
                    </div>
                  </div>
                </div>
              </div>

              <!-- OU2 Lines Table -->
              <div>
                <h3 class="text-lg font-semibold text-gray-800 mb-4 text-center">
                  {{ event?.ocAwayTeamName || event?.away || 'Away' }} - OU2 Lines
                </h3>
                <div v-if="ou2Lines.length === 0" class="text-center py-8 text-gray-500">
                  No lines available
                </div>
                
                <div v-else class="space-y-2">
                  <!-- Table Header -->
                  <div class="grid grid-cols-4 gap-2 pb-2 border-b border-gray-200 font-semibold text-xs text-gray-600">
                    <div>Line</div>
                    <div class="text-green-600">Over</div>
                    <div class="text-red-600">Under</div>
                    <div>Diff</div>
                  </div>
                  
                  <!-- Table Rows -->
                  <div
                    v-for="(line, index) in ou2Lines"
                    :key="index"
                    :class="[
                      'grid grid-cols-4 gap-2 py-2 px-2 rounded-lg transition-colors text-sm',
                      line.isBalanced 
                        ? 'bg-blue-50 border-2 border-blue-500 font-semibold' 
                        : 'hover:bg-gray-50'
                    ]"
                  >
                    <div :class="['font-medium', line.isBalanced ? 'text-blue-700' : 'text-gray-900']">
                      {{ line.line }}
                      <span v-if="line.isBalanced" class="ml-1 text-xs text-blue-600">
                        <i class="fas fa-star"></i>
                      </span>
                    </div>
                    <div :class="['text-green-600', line.isBalanced ? 'font-bold' : 'font-medium']">
                      {{ line.over }}
                    </div>
                    <div :class="['text-red-600', line.isBalanced ? 'font-bold' : 'font-medium']">
                      {{ line.under }}
                    </div>
                    <div :class="['text-gray-600 text-xs', line.isBalanced ? 'font-bold' : '']">
                      {{ line.diff.toFixed(3) }}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Footer -->
          <div class="p-4 border-t border-gray-200 bg-gray-50 text-sm text-gray-500 text-center">
            <div v-if="event?.name" class="mb-1">
              {{ event.name }}
            </div>
            <div v-if="event?.updatedAt">
              Last updated: {{ new Date(event.updatedAt * 1000).toLocaleString() }}
            </div>
          </div>
        </div>
      </Transition>
    </div>
  </Transition>
</template>

<style scoped>
/* Component styles */
</style>
